import { useEffect, useState } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { useCoachAcademyStore } from '@/store/coachAcademy';
import { academyService } from '@/services/academy';
import { AcademyBrandingProvider } from '@/contexts/AcademyBrandingContext';
import { useAcademyHost } from '@/contexts/AcademyHostContext';
import { useAcademySlug } from '@/hooks/useAcademySlug';
import { DashboardLayout } from './DashboardLayout';
import { hasRole, TierFeatureKey, normalizeSubscriptionTier } from '@court-sync/shared';
import { useCoachAcademyFeatureAccess } from '@/hooks/useFeatureAccess';
import { useAcademyPaths } from '@/hooks/useAcademyPaths';
import { StarterPortalUnavailable } from '@/components/guardian/StarterPortalUnavailable';

export function CoachAcademyLayout() {
    const academySlug = useAcademySlug();
    const { host, academy: hostAcademy } = useAcademyHost();
    const { user } = useAuthStore();
    const {
        academySlug: storeSlug,
        academy,
        setAcademyContext,
        clearAcademyContext,
    } = useCoachAcademyStore();
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!academySlug) {
            clearAcademyContext();
            return undefined;
        }
        if (host.mode === 'subdomain' && hostAcademy) {
            void Promise.resolve().then(() => {
                setAcademyContext(academySlug, hostAcademy.id, hostAcademy);
                setError(null);
            });
            return () => clearAcademyContext();
        }
        let mounted = true;
        academyService
            .resolveAcademyBySlug(academySlug)
            .then((acad) => {
                if (mounted) {
                    setAcademyContext(academySlug, acad.id, acad);
                    setError(null);
                }
            })
            .catch((err) => {
                if (mounted) {
                    setError(err instanceof Error ? err.message : 'Academy not found');
                }
            });
        return () => {
            mounted = false;
            clearAcademyContext();
        };
    }, [academySlug, host.mode, hostAcademy, setAcademyContext, clearAcademyContext]);

    const loading = !!academySlug && !error && (storeSlug !== academySlug || !academy);
    const { hasAccess: hasCoachPortal } = useCoachAcademyFeatureAccess(
        academy,
        academySlug,
        TierFeatureKey.COACH_PORTAL
    );
    const { p } = useAcademyPaths(academy ?? { slug: academySlug ?? '' });

    if (!user || !hasRole(user, 'coach')) {
        return <Navigate to="/dashboard" replace />;
    }

    if (!academySlug) {
        return <Navigate to="/coach/academy-picker" replace />;
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <p className="text-gray-600 dark:text-gray-400">Loading academy...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                    <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
                    <a href="/coach/academy-picker" className="text-primary-600 hover:underline">
                        Back to academy picker
                    </a>
                </div>
            </div>
        );
    }

    if (academy && !hasCoachPortal && normalizeSubscriptionTier(academy.subscriptionTier) === 'starter') {
        return (
            <AcademyBrandingProvider primaryColor={academy.primaryColor} secondaryColor={academy.secondaryColor}>
                <DashboardLayout
                    dashboardRole="coach"
                    academySlug={academySlug}
                    academyName={academy.name}
                    academyLogoUrl={academy.logoUrl}
                >
                    <StarterPortalUnavailable
                        academy={academy}
                        portalLabel="Coach portal"
                        publicLessonsPath={p('/lessons')}
                    />
                </DashboardLayout>
            </AcademyBrandingProvider>
        );
    }

    return (
        <AcademyBrandingProvider primaryColor={academy!.primaryColor} secondaryColor={academy!.secondaryColor}>
            <DashboardLayout
                dashboardRole="coach"
                academySlug={academySlug}
                academyName={academy!.name}
                academyLogoUrl={academy!.logoUrl}
            >
                <Outlet />
            </DashboardLayout>
        </AcademyBrandingProvider>
    );
}
